import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import Stack from '@mui/material/Stack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Unstable_Grid2';

import PageContent from '../../../components/Redesing/page-content';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CardView from '../../../components/Redesing/card-view';
import RentalsTableCard from '../../../components/Redesing/table/rental/rentals-table-card';
import PageContainer from '../../../components/Redesing/page-container';

import { IStation, StationConfiguration } from '../../../ts/interface/station.interface';

import { changeTab, findAccess, history, toTitleCase } from '../../../utils';
import { getLocationIconByName } from '../../../util/utilsR';

import { ACTIVE, RETIRED } from '../../../constants';

import LocationDetailsCard from './locationDetailsCard';
import LocationStatsCard from './locationStatsCard';
import CustomMapComponent from '../../../components/Redesing/custom-map-component-for-detail-location';
import HardwareLocationCard from './hardwareLocationCard';
import RatesLocationCard from './ratesLocationCard';
import LocationOperatingHoursCard from '../locationOperatingHoursCard';
import useOrganization from '../../../hooks/use-organization';
import {
  useLocationDetailRelayQuery,
  mapLocationDetailToIStation,
  mapLocationToLocationStats,
} from '../../../hooks/stations/query/V2';
import { useSystemRelay } from '../../../hooks/system/query/V2/useSystemRelay';
import { ISystem } from '../../../ts/interface/system.interface';
import { IMode } from '../../../ts/interface/mode.interface';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { retireStation } from '../../../api/stations';
import SaveIcon from '@mui/icons-material/Save';

function LocationDetails() {
  const organizationInfo = useOrganization();
  const stationId = useParams<{ stationId: string }>().stationId;
  const [activeHardware, setActiveHardware] = useState(false);
  const [retireModal, setRetireModal] = useState(false);
  const [currentTabV, setCurrentTabV] = useState('general');

  const currentTab = useParams<{ currentTab: string }>().currentTab || 'general';

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const { data: locationData, isLoading: isLoadingStations } =
    useLocationDetailRelayQuery(stationId, { enabled: !!stationId });

  const detailedStation = useMemo(() => {
    if (locationData) {
      return mapLocationDetailToIStation(locationData);
    }
    return {} as IStation;
  }, [locationData]);

  const locationStats = useMemo(() => {
    if (locationData) {
      return mapLocationToLocationStats(locationData);
    }
    return null;
  }, [locationData]);

  // Source system + modeInfo from GraphQL (getSystem by node id), keyed off the
  // location's own system. This works in the organization view, which has no
  // selected system, so it must not call the system-scoped /currentsystem REST
  // endpoint that useCurrentSystemInfo used.
  const { system, modeInfo } = useSystemRelay(
    detailedStation?.system_info?.id ?? undefined,
    { enabled: Boolean(detailedStation?.system_info?.id) }
  );

  // Hardware at this location comes from GraphQL
  const hardwareAtLocation = useMemo(() => {
    return detailedStation.hardware || [];
  }, [detailedStation.hardware]);

  const handleTabsChange = useCallback(
    (event: any, value: React.SetStateAction<string>) => {
      changeTab(`${stationId}/${value}`, currentTab, 'locations');
    },
    [stationId, currentTab]
  );

  const warningCheck = () => {
    if (!detailedStation.module && modeInfo?.currentMode === 'bikeroom') {
      return `This ${modeInfo?.details?.stationsTitle} is not available to make rentals. Assign a lock to make this ${modeInfo?.details?.stationsTitle} available to make rentals`;
    }
  };

  const getAccess = (section: string) => {
    return !findAccess(organizationInfo.access).includes(section);
  };

  const topActionButtons = () => {
    const { state, id } = detailedStation;
    const bts = [
      {
        startIcon: state === RETIRED ? <LocationOnIcon /> : <EditIcon />,
        label: state === RETIRED ? 'Unretire' : 'Edit',
        onClick: () => history.push(`/locations/${id}/edit`),
        disabled: getAccess('physical'),
      },
    ];
    if (state !== RETIRED) {
      bts.push({
        startIcon: <LocationOffIcon />,
        label: 'Retire',
        onClick: () => checkForActiveHardware(),
        disabled: getAccess('physical'),
      });
    }
    return bts;
  };

  const checkForActiveHardware = () => {
    const { ZONE } = StationConfiguration;

    // Use hardware from GraphQL location data
    const bikesAtStation = hardwareAtLocation.filter((item) => {
      return (
        item.state === ACTIVE &&
        (system.new_hardware ? item.type_name !== 'base_station' : true)
      );
    });

    if (detailedStation.configuration !== ZONE && bikesAtStation.length > 0) {
      setActiveHardware(true);
    } else {
      setActiveHardware(false);
    }
    setRetireModal(true);
  };

  const getSecondContent = () => {
    return (
      <>
        {Boolean(system?.gid) && Boolean(modeInfo) && (
          <CustomMapComponent
            system={system as unknown as ISystem}
            modeInfo={modeInfo as IMode}
            currentLocation={detailedStation}
          />
        )}
        {currentTab !== 'rentals' && (
          <CardView
            isLoading={isLoadingStations}
            content={<HardwareLocationCard detailedStation={detailedStation} />}
            title={`${modeInfo?.details.pluralUnits || 'Units'} at this ${
              modeInfo?.details.stationsTitle || 'Location'
            }`}
          />
        )}
        {currentTab === 'general' && system.new_hardware && (
          <CardView
            isLoading={isLoadingStations}
            content={<RatesLocationCard detailedStation={detailedStation} />}
            title={`Rates at this ${modeInfo?.details.stationsTitle || 'Location'}`}
          />
        )}
      </>
    );
  };

  const modal = () => {
    const { stationsTitle, unitsTitle } = modeInfo?.details ?? {};
    return (
      <>
        <MovaticCustomModal
          open={retireModal}
          onClose={() => setRetireModal(!retireModal)}
          title={`Retire ${stationsTitle}?`}
          content={`Are you sure you would like to retire this ${stationsTitle?.toLowerCase()}?
          ${
            activeHardware
              ? ` Any ${unitsTitle.toLowerCase()} at this ${stationsTitle?.toLowerCase()} will need to be assigned to a new ${stationsTitle?.toLowerCase()} in order to be accessible to users again.`
              : ''
          }`}
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => setRetireModal(!retireModal),
                },
                {
                  label: 'Save',
                  onClick: () => {
                    CustomAsyncToast({
                      promise: () => retireStation(detailedStation.id),
                      successMessage: () => `${stationsTitle} retired successfully`,
                      errorMessage: `Error retiring ${stationsTitle}`,
                      loadingMessage: `Retiring ${stationsTitle}...`,
                    }).then((r) => {
                      if (r) {
                        history.push('/locations');
                      }
                    });
                  },
                  startIcon: <SaveIcon />,
                },
              ]}
            />
          }
          customStyles={undefined}
          useContentDividers={undefined}
        />
      </>
    );
  };

  return (
    <PageContainer warningMessage={warningCheck()} isLoading={isLoadingStations}>
      <>
        <PageContent
          isLoading={isLoadingStations}
          backLink={'/locations'}
          chipLabel={modeInfo?.details.stationsTitle || 'Location'}
          contentTitle={'Locations'}
          itemName={toTitleCase(detailedStation?.name)}
          itemChipValue={detailedStation?.id}
          detailIcon={
            detailedStation?.configuration ? (
              getLocationIconByName(detailedStation?.configuration).icon
            ) : (
              <LocationOnIcon />
            )
          }
          itemActions={null}
          itemTopButtons={<IconButtonMenu buttons={topActionButtons()} />}
          handleTabsChange={handleTabsChange}
          currentTabV={currentTabV}
          tabsList={[
            { label: 'Details', value: 'general' },
            { label: 'Rentals', value: 'rentals' },
          ]}
          tabsChildren={
            <>
              {currentTabV === 'general' && (
                <Grid container spacing={2}>
                  <Grid xs={12} lg={4}>
                    <Stack spacing={2}>
                      <CardView
                        isLoading={isLoadingStations}
                        content={
                          <LocationDetailsCard detailedStation={detailedStation} />
                        }
                        title={'Location Details'}
                      />
                      <LocationOperatingHoursCard
                        location={locationData}
                        isLoading={isLoadingStations}
                      />
                      {locationStats && (
                        <CardView
                          isLoading={isLoadingStations}
                          content={<LocationStatsCard stats={locationStats} />}
                          title={'Location Stats'}
                        />
                      )}
                    </Stack>
                  </Grid>
                  <Grid xs={12} lg={8}>
                    <Stack spacing={2}>{getSecondContent()}</Stack>
                  </Grid>
                </Grid>
              )}
              {currentTab === 'rentals' && (
                <>
                  <RentalsTableCard
                    noRentalsText={
                      'This location does not have any rentals matching the criteria'
                    }
                    parameters={{ station_id: detailedStation.old_id }}
                  />
                </>
              )}
            </>
          }
        />
        {modal()}
      </>
    </PageContainer>
  );
}

export default LocationDetails;
